import styled from 'styled-components';
import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Typography, Badge } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { getInvoiceDisputeStatus, getInvoiceUpdateStatus } from 'utils/buildingInvoiceDataSource';
import { isInvoicePaid } from 'utils/invoicePaymentStatus';
import { getInvoicePDFAction } from 'store/actions/invoiceActions';
import { failedNotif } from 'store/actions/notifActions';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  INVOICES_TABLE_DEFAULT_VISIBLE_KEYS,
  INVOICES_TABLE_STORAGE_KEY,
} from './invoiceTableConfig';

const LEAP_PURPLE = '#702572';
const DISABLED_ICON_COLOR = '#B8B0BC';

const dateColumnOptions = (overrides = {}) => ({
  width: 110,
  shrink: false,
  ...overrides,
});

export const InvoiceTable = forwardRef(
  (
    {
      invoices,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickInvoice,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      onLoadFullDatasetTotal,
      isLoadingFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      showPdfDownload = true,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    const pdfLoadingInvoiceId = useSelector((state) => state.getIn(['invoice', 'pdf_loading']));
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    const buildInvoiceRow = useCallback(
      (invoice, index) => {
        const schedule_date = invoices?.schedule_date;
        const update_status = getInvoiceUpdateStatus(invoice, schedule_date) || null;
        const dispute_status = getInvoiceDisputeStatus(invoice) || null;

        return {
          sf_building_name: isDemo ? getDemoData('building-name') : invoice.sf_building_name,
          invoice_id: isDemo ? getDemoData('invoice-number') : invoice.doc_number,
          payment: isInvoicePaid(invoice.balance),
          invoice_status: invoice.invoice_status,
          total_amount: invoice.total_amount,
          balance: invoice.balance,
          due_date: invoice.due_date,
          dispute_status,
          sf_landlord_name: isDemo ? getDemoData('landlord-name') : invoice.sf_landlord_name,
          lastUpdatedTime: invoice.lastUpdatedTime,
          update_status,
          actions: index,
        };
      },
      [invoices?.schedule_date, isDemo],
    );

    const getInvoiceFromRow = useCallback(
      (row) => {
        if (!row || !invoices?.invoiceData) return null;
        const rowIndex =
          typeof row === 'object' && !Array.isArray(row) ? row.actions : row?.[row.length - 1];
        return invoices.invoiceData[rowIndex] ?? null;
      },
      [invoices?.invoiceData],
    );

    const tableData = useMemo(() => {
      if (!invoices?.invoiceData) return [];
      return invoices.invoiceData.map((invoice, index) => buildInvoiceRow(invoice, index));
    }, [buildInvoiceRow, invoices?.invoiceData]);

    const goDetailPage = useCallback(
      (row) => {
        const invoice = getInvoiceFromRow(row);
        if (invoice && typeof onClickInvoice === 'function') {
          onClickInvoice(invoice);
        }
      },
      [getInvoiceFromRow, onClickInvoice],
    );

    const handlePdfDownload = useCallback(
      (row) => {
        if (isDemo) {
          dispatch(failedNotif('Invoice PDF download is disabled while demo mode is on.'));
          return;
        }
        const invoice = getInvoiceFromRow(row);
        if (!invoice?.invoice_id) {
          dispatch(failedNotif('Invoice PDF is unavailable for this row.'));
          return;
        }
        if (`${pdfLoadingInvoiceId}` === `${invoice.invoice_id}`) return;
        dispatch(
          getInvoicePDFAction({
            invoiceId: invoice.invoice_id,
            invoice,
          }),
        );
      },
      [dispatch, getInvoiceFromRow, isDemo, pdfLoadingInvoiceId],
    );

    const columns = useMemo(
      () => [
        {
          name: 'Property Name',
          key: 'sf_building_name',
          options: {
            minWidth: 160,
            flexGrow: 1,
            ellipsis: false,
            sort: true,
          },
        },
        {
          name: 'Invoice Number',
          key: 'invoice_id',
          options: {
            ...fitContentTextColumnOptions({ fitContentCharWidth: 8, maxWidth: 140, flexGrow: 0 }),
            sort: true,
          },
        },
        {
          name: 'Payment',
          key: 'payment',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 100 }),
            sort: true,
            exportValue: (value) => (value === true ? 'Paid' : 'Unpaid'),
            customBodyRenderer: (value) => (
              <Badge
                color="magentaRed"
                label={value == true ? 'Paid' : 'Unpaid'}
                rounded
                textSize="medium"
              />
            ),
          },
        },
        {
          name: 'Status',
          key: 'invoice_status',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 120 }),
            sort: true,
            customBodyRenderer: (value) => (
              <Badge color="neutral" label={value} rounded textSize="medium" />
            ),
          },
        },
        {
          name: 'Total Amount',
          key: 'total_amount',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 110, align: 'center' }),
            sort: true,
          },
        },
        {
          name: 'Balance',
          key: 'balance',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 110, align: 'center' }),
            sort: true,
          },
        },
        {
          name: 'Due Date',
          key: 'due_date',
          options: {
            ...dateColumnOptions(),
            sort: true,
          },
        },
        {
          name: 'Disputed',
          key: 'dispute_status',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 72, maxWidth: 88 }),
            customBodyRenderer: (value) => (
              <Badge
                color={value ? 'parisGreen' : 'tomato'}
                label={value == 'dispute' ? 'Yes' : 'No'}
                rounded
                textSize="medium"
              />
            ),
          },
        },
        {
          name: 'Operator Name',
          key: 'sf_landlord_name',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 280 }),
            sort: true,
          },
        },
        {
          name: 'Last Updated Time',
          key: 'lastUpdatedTime',
          options: {
            ...dateColumnOptions({ width: 130 }),
            sort: true,
            customBodyRenderer: (value) => (
              <Typography align="center" variant="body2">
                {value?.split('T')[0]}
              </Typography>
            ),
          },
        },
        {
          name: 'Update Status',
          key: 'update_status',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 96, maxWidth: 120 }),
            customBodyRenderer: (value) => {
              if (value) {
                return <Badge color="warmBlue" label={value} rounded textSize="medium" />;
              }
              return null;
            },
          },
        },
        {
          name: 'Actions',
          key: 'actions',
          options: {
            width: 112,
            minWidth: 112,
            maxWidth: 112,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (_value, row) => {
              const invoice = getInvoiceFromRow(row);
              const invoiceLink = invoice?.invoice_link;
              const hasLink = Boolean(invoiceLink);
              const pdfAvailable = showPdfDownload && Boolean(invoice?.invoice_id) && !isDemo;
              const isPdfLoading =
                pdfLoadingInvoiceId != null &&
                `${pdfLoadingInvoiceId}` === `${invoice?.invoice_id}`;

              return (
                <ActionsCell onClick={(e) => e.stopPropagation()}>
                  <ActionIconButton
                    icon="FileFolder.Description"
                    title="Invoice details"
                    disabled={!invoice}
                    onClick={() => goDetailPage(row)}
                  />
                  <ActionIconButton
                    icon="Arrow.NorthEast"
                    title="Open invoice link"
                    disabled={!hasLink}
                    onClick={() => {
                      if (hasLink) {
                        window.open(invoiceLink, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  />
                  {isPdfLoading ? (
                    <ActionLoadingButton title="Downloading PDF..." />
                  ) : (
                    <ActionIconButton
                      icon="FileFolder.Pdf"
                      title="PDF"
                      disabled={!pdfAvailable}
                      onClick={() => handlePdfDownload(row)}
                    />
                  )}
                </ActionsCell>
              );
            },
          },
        },
      ],
      [
        getInvoiceFromRow,
        goDetailPage,
        handlePdfDownload,
        isDemo,
        pdfLoadingInvoiceId,
        showPdfDownload,
      ],
    );

    return (
      <>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText ? 'No invoices match your search.' : 'No invoices available yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          autoTooltipForDefaultCells
          defaultVisibleColumnKeys={INVOICES_TABLE_DEFAULT_VISIBLE_KEYS}
          columnVisibilityStorageKey={INVOICES_TABLE_STORAGE_KEY}
          columnWidthStorageKey={INVOICES_TABLE_STORAGE_KEY}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((invoice, index) => buildInvoiceRow(invoice, index));
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((invoice, index) => buildInvoiceRow(invoice, index));
                }
              : undefined
          }
          data={tableData}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Invoice"
          columns={columns}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Invoice"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          onLoadFullDatasetTotal={onLoadFullDatasetTotal}
          isLoadingFullDatasetTotal={isLoadingFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

InvoiceTable.displayName = 'InvoiceTable';

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  min-height: 24px;
`;

const ActionLoadingButton = ({ title }) => (
  <Tooltip title={title} placement="top" showCarot>
    <span>
      <ActionIcon as="span" aria-label={title}>
        <ActionSpinner aria-hidden="true" />
      </ActionIcon>
    </span>
  </Tooltip>
);

const ActionSpinner = styled.span`
  box-sizing: border-box;
  display: block;
  width: 18px;
  height: 18px;
  border: 2px solid #f3f1f4;
  border-top-color: #90388b;
  border-radius: 50%;
  animation: invoice-pdf-spin 0.9s linear infinite;

  @keyframes invoice-pdf-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ActionIconButton = ({ icon, title, disabled, onClick }) => (
  <Tooltip title={title} placement="top" showCarot>
    <span>
      <ActionIcon
        type="button"
        disabled={disabled}
        aria-label={title}
        onClick={disabled ? undefined : onClick}
      >
        <IconGraphy icon={icon} style={{ color: disabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }} />
      </ActionIcon>
    </span>
  </Tooltip>
);

const ActionIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;
